import { motion } from 'framer-motion';
import { 
  LockKeyhole, 
  Database, 
  Cloud, 
  Server, 
  Eye, 
  Target, 
  Radar, 
  Shield,
  Globe,
  Zap
} from 'lucide-react';
import SecurityStory from './SecurityStory';

const BodySection = () => {
  return (
      <div className="space-y-16 py-8 px-4 md:px-8">
        {/* Feature Highlights */}
        <section className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
          </motion.div>
        </section>

        {/* Security Story Section */}
        <SecurityStory />

        {/* Cloud Native & Agentless Section */}
        <section className="relative">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50/50 to-white" />
          
          {/* Content */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      Cloud Native
                    </h3>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Built for cloud environments, ensuring seamless integration and 
                    optimal performance in your infrastructure.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      Agentless
                    </h3>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    No agents to install or maintain. Deploy instantly and start protecting your data without any infrastructure changes.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        

        {/* CISO Vision Section */}
        <section className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="relative w-full bg-gradient-background overflow-hidden rounded-3xl">
            {/* Background gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-secondary/10 to-accent/10" />
            
            {/* Content */}
            <div className="relative z-20 p-8 md:p-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl mx-auto text-center mb-12"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Revolutionizing Data Security
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed">
                Reimagining data security for the cloud-first world.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-white space-y-6"
                >
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold mb-4">
                      "You can't protect what you can't see"
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                    Visibility is everything—because what you can't see will hurt you. ANCILE cuts through the fog, mapping your entire IT landscape and locking down every data source. No blind spots. No surprises. Just total clarity and control, so you can lead your business forward without looking over your shoulder.</p>
                  </div>
                  
                  <div className="flex gap-4 mt-8">
                    <button 
                      onClick={() => window.open('/contact')}
                      className="bg-gradient-primary hover:bg-gradient-secondary text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-glow"
                    >
                      Learn More
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="relative"
                >
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { icon: Eye, label: "Complete Visibility", color: "bg-blue-500" },
                      { icon: Shield, label: "Proactive Defense", color: "bg-purple-500" },
                      { icon: Database, label: "Data Protection", color: "bg-green-500" },
                      { icon: LockKeyhole, label: "Access Governance", color: "bg-red-500" }
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-6 flex flex-col items-center text-center border border-white/10"
                      >
                        <div className={`${item.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                          <item.icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-white text-sm font-medium">{item.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
};

export default BodySection; 